import type { ListQuery, ListResponse } from '@shawwir/contracts';

/**
 * The one and only place the SPA talks to the network.
 *
 * The legacy app hard-coded its API origin in 25+ files. Here the base URL comes
 * from runtime config injected by the container, and there is exactly one fetch
 * wrapper, so auth handling, error shape and credentials are uniform.
 */

declare global {
  interface Window {
    __SHAWWIR_ENV__?: { BFF_URL?: string };
  }
}

const BASE_URL =
  window.__SHAWWIR_ENV__?.BFF_URL ?? (import.meta.env.VITE_BFF_URL as string | undefined) ?? '/bff';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    override readonly message: string,
    /** Field-level errors, ready to be mapped straight onto form inputs (REQ-9). */
    readonly fields?: Record<string, string>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** AUTH-6: distinguishes a dead session from every other failure. */
  get isAuthError() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNetworkError() {
    return this.status === 0;
  }
}

type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

/** Lets the auth provider bounce the user to /login on any 401, from anywhere. */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null) {
  onSessionExpired = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Set for the login call, which must not trigger the session-expired bounce. */
  skipAuthRedirect?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
      // The session cookie is httpOnly; the browser must be told to send it.
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // Status 0 means we never reached the server -- worth saying so plainly
    // rather than showing a generic failure (SHL-9).
    throw new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    payload = undefined;
  }

  if (!response.ok) {
    const body = payload as
      | { error?: { code?: string; message?: string; fields?: Record<string, string>; requestId?: string } }
      | undefined;

    const error = new ApiError(
      response.status,
      body?.error?.code ?? 'error',
      body?.error?.message ?? response.statusText ?? 'Request failed',
      body?.error?.fields,
      body?.error?.requestId,
    );

    if (error.isAuthError && !options.skipAuthRedirect) onSessionExpired?.();
    throw error;
  }

  return payload as T;
}

/** Serialises a ListQuery into the query string every list route understands. */
export function listQueryToParams(query: Partial<ListQuery>): URLSearchParams {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.dir) params.set('dir', query.dir);
  if (query.q) params.set('q', query.q);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  for (const [key, value] of Object.entries(query.filter ?? {})) {
    if (value !== undefined && value !== '') params.set(`filter[${key}]`, value);
  }
  return params;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, opts?: { skipAuthRedirect?: boolean }) =>
    request<T>(path, { method: 'POST', body, ...opts }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /** Typed helper for the uniform list endpoints. */
  list: <T>(entity: string, query: Partial<ListQuery>, signal?: AbortSignal) => {
    const params = listQueryToParams(query);
    const qs = params.toString();
    return request<ListResponse<T>>(`/${entity}${qs ? `?${qs}` : ''}`, signal ? { signal } : {});
  },
};

/** Builds a download URL for the streamed exports (TBL-12). */
export function exportUrl(
  entity: string,
  format: 'csv' | 'xlsx',
  query: Partial<ListQuery>,
  columns?: string[],
): string {
  const params = listQueryToParams(query);
  if (columns?.length) params.set('columns', columns.join(','));
  const qs = params.toString();
  return `${BASE_URL}/export/${entity}.${format}${qs ? `?${qs}` : ''}`;
}
